import { Injectable } from '@angular/core';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import {
  AmountOperatorsValues,
  RulesActions,
  RulesAmountType,
  RulesConditions,
  RulesOperator,
  RulesTag,
} from '../../utils';
import { AutomationRulesActions } from '../../actions';

@Injectable({ providedIn: 'root' })
export class RuleTemplateService {
  maximumAutocompleteWidth: number = 400;

  constructor() {}

  selectDeliveryMethod({ value: deliveryMethod }, index, currentConditionsSelectValue): any {
    const deliveryMethodCondition = {
      ...currentConditionsSelectValue[index],
    };
    deliveryMethodCondition.value = deliveryMethod;
    return deliveryMethodCondition;
  }

  getDeliveryMethodOperatorText(operator: string): string {
    switch (operator) {
      case 'is':
        return 'Any of';
      case 'is-not':
        return 'Any of';
      case 'contains':
        return 'Any of';
      case 'not-contains':
        return 'All of';
      default:
        return 'Any of';
    }
  }

  changeInventorySelect(value, index, currentConditionsSelectValue: any, currentOperatorValue) {
    const inventoryLocationCondition: any = {
      ...currentConditionsSelectValue[index],
    };
    const operator = currentOperatorValue[index];
    const address = value;
    inventoryLocationCondition.value = {
      operator,
      address,
    };
    return inventoryLocationCondition;
  }

  changeDeliveryStatusSelect(value, index, currentConditionsSelectValue: any, currentDeliveryStatusValue) {
    const deliveryStatusCondition: any = {
      ...currentConditionsSelectValue[index],
    };
    let deliveryStatus = [...(currentDeliveryStatusValue[index] ?? [])];
    if (value.checked) {
      deliveryStatus.push(value);
    } else {
      deliveryStatus = deliveryStatus.filter((o) => o.value != value.value);
    }
    currentDeliveryStatusValue[index] = deliveryStatus;
    deliveryStatusCondition.value = { deliveryStatus };
    return deliveryStatusCondition;
  }

  changeFulfillmentStatusSelect(value, index, currentConditionsSelectValue: any) {
    const fulfillmentStatusCondition: any = {
      ...currentConditionsSelectValue[index],
    };
    const fulfillmentStatus = value;
    fulfillmentStatusCondition.value = {
      fulfillmentStatus,
    };
    return fulfillmentStatusCondition;
  }

  changeShipOnDateSelect(
    value,
    index,
    currentConditionsSelectValue: any,
    currentOperatorValue,
    currentShipOnDateValue,
  ) {
    let updatedValue: {
      value: string;
      label: string;
      dayType?: { value: string; label: string };
      dayNumber: number;
    };
    switch (value?.property) {
      case 'x':
        updatedValue = {
          dayNumber: value?.value,
          value: 'in X days',
          dayType: currentShipOnDateValue?.value?.dayType,
          label: 'in X days',
        };
        break;
      case 'dayType':
        updatedValue = {
          value: 'in X days',
          dayNumber: currentShipOnDateValue?.value?.dayNumber,
          dayType: { label: value?.value.label, value: value?.value.value },
          label: 'in X days',
        };
        break;
      case 'ShipOnDateValue':
        switch (value?.value?.label) {
          case 'today':
            updatedValue = {
              value: 'today',
              dayNumber: 0,
              label: 'today',
            };
            break;
          case 'tomorrow':
            updatedValue = {
              value: 'tomorrow',
              dayNumber: 1,
              label: 'tomorrow',
            };
            break;
          case 'in X days':
            updatedValue = {
              value: 'in X days',
              dayNumber: currentShipOnDateValue?.value?.dayNumber ?? 0,
              dayType: {
                value: currentShipOnDateValue?.value?.dayType?.value ?? '0',
                label: currentShipOnDateValue?.value?.dayType?.label ?? 'days',
              },
              label: 'in X days',
            };
            break;
        }
        break;
    }

    const shipOnDateCondition: any = {
      ...currentConditionsSelectValue[index],
    };
    const operator = currentOperatorValue[index];

    shipOnDateCondition.value = {
      operator,
      value: updatedValue,
    };
    return shipOnDateCondition;
  }

  changePackageSelect(value, index, currentConditionsSelectValue: any, currentOperatorValue, currentPackageValue) {
    const packageCondition: any = {
      ...currentConditionsSelectValue[index],
    };
    const operator = currentOperatorValue[index];
    let boxesArray = [...(currentPackageValue[index] ?? [])];
    if (value.checked) {
      boxesArray.push(value);
    } else {
      boxesArray = boxesArray.filter((box) => box.value != value.value);
    }
    currentPackageValue[index] = boxesArray;
    packageCondition.value = {
      operator,
      boxesArray,
    };
    return packageCondition;
  }

  changePaymentStatusSelect(
    value,
    index,
    currentConditionsSelectValue: any,
    currentOperatorValue,
    currentPaymentValue,
  ) {
    const paymentCondition: any = {
      ...currentConditionsSelectValue[index],
    };
    let statuses = currentPaymentValue[index] ? [...currentPaymentValue[index]] : [];
    if (value.checked) {
      statuses.push(value);
    } else {
      statuses = statuses.filter((status) => status.value != value.value);
    }
    currentPaymentValue[index] = statuses;
    paymentCondition.value = {
      statuses,
    };
    return paymentCondition;
  }

  changeCreateWeekDateSelect(value, index, currentConditionsSelectValue: any, currentCreatedOnValue) {
    let weekDaysArray = [...(currentCreatedOnValue[index].weekDays ?? [])];
    if (value.checked) {
      weekDaysArray.push(value);
    } else {
      weekDaysArray = weekDaysArray.filter((day) => day.value != value.value);
    }

    return this.changeCreateTimeSelect(
      { weekDays: weekDaysArray },
      index,
      currentConditionsSelectValue,
      currentCreatedOnValue,
    );
  }

  changeCreateTimeOfDaySelect(
    value,
    type,
    index,
    timeOfDayOptions,
    currentConditionsSelectValue: any,
    currentCreatedOnValue,
  ) {
    const update = { [type]: value };
    if (type === 'from' && currentCreatedOnValue[index].to.value <= value.value) {
      update['to'] = timeOfDayOptions[timeOfDayOptions.indexOf(value) + 1];
    }
    return this.changeCreateTimeSelect(update, index, currentConditionsSelectValue, currentCreatedOnValue);
  }

  changeCreateTimeSelect(update, index, currentConditionsSelectValue: any, currentCreatedOnValue) {
    const createOnCondition: any = {
      ...currentConditionsSelectValue[index],
    };
    currentCreatedOnValue[index] = { ...currentCreatedOnValue[index], ...update };
    createOnCondition.value = currentCreatedOnValue[index];
    return createOnCondition;
  }

  onAmountTyped(
    amountTyped: number,
    index: number,
    type: RulesAmountType,
    currentConditionsSelectValue,
    currentOperatorValue,
    currentWeightValue,
    currentItemQuantityValue,
    currentOrderValueVal,
  ): any {
    const amountCondition = {
      ...currentConditionsSelectValue[index],
    };
    const operator = currentOperatorValue[index];
    const amount = amountTyped && +amountTyped != 0 ? amountTyped : 0.0;
    switch (type) {
      case RulesAmountType.WEIGHT:
        currentWeightValue[index] = amount;
        break;
      case RulesAmountType.ITEM_QUANTITY:
        currentItemQuantityValue[index] = amount;
        break;
      case RulesAmountType.TOTAL_VALUE:
        currentOrderValueVal[index] = amount;
        break;
    }
    amountCondition.value = {
      operator,
      amount,
    };
    return amountCondition;
  }

  isTextOverflowed(event: HTMLElement | any) {
    const offsetWidth = event?.elementRef.nativeElement?.firstElementChild?.offsetWidth ?? 0;
    return !(offsetWidth < this.maximumAutocompleteWidth);
  }

  onTagSelect(
    index: number,
    tag: RulesTag,
    controls,
    currentConditionsSelectValue,
    currentSelectedTags,
    currentOperatorValue,
    currentOperator2Value,
  ) {
    controls[index].reset('', { onlySelf: true });

    const tagsCondition = {
      ...currentConditionsSelectValue[index],
    };
    const operator = currentOperatorValue[index];
    const operator2 = currentOperator2Value[index];
    const newSelectedTags: RulesTag[] = [...currentSelectedTags[index]];
    const optionExists = currentSelectedTags[index]?.find((val) => val.name === tag.name);
    if (!optionExists) {
      newSelectedTags.push(tag);
      currentSelectedTags[index] = newSelectedTags;
      const selectedTags = [...newSelectedTags];
      tagsCondition.value = {
        selectedTags,
        operator,
        operator2,
      };
    }
    return { optionExists, tagsCondition };
  }

  onTagRemove(
    conditionIndex: number,
    tagIndex: number,
    currentConditionsSelectValue,
    currentSelectedTags,
    currentOperatorValue,
    currentOperator2Value,
  ): any {
    // must use copy array because we get the real ref to filterValueArray from table
    const tagsCondition = {
      ...currentConditionsSelectValue[conditionIndex],
    };
    const operator = currentOperatorValue[conditionIndex];
    const operator2 = currentOperator2Value[conditionIndex];
    const removeSelectedTags: RulesTag[] = [...currentSelectedTags[conditionIndex]];
    removeSelectedTags.splice(tagIndex, 1);
    const selectedTags = [...removeSelectedTags];
    tagsCondition.value = {
      selectedTags,
      operator,
      operator2,
    };
    currentSelectedTags[conditionIndex] = removeSelectedTags;
    return tagsCondition;
  }

  onSkuSelect(
    index: number,
    sku: string,
    controls,
    currentConditionsSelectValue,
    currentSelectedSkus,
    currentOperatorValue,
    currentOperator2Value,
  ) {
    controls[index].reset('', { onlySelf: true });

    const operator = currentOperatorValue[index];
    const operator2 = currentOperator2Value[index];
    const skusCondition = {
      ...currentConditionsSelectValue[index],
    };
    const newSelectedSkus: string[] = [...currentSelectedSkus[index]];
    const optionExists = currentSelectedSkus[index]?.find((val) => val === sku);
    if (!optionExists) {
      newSelectedSkus.push(sku);
      currentSelectedSkus[index] = newSelectedSkus;
      const selectedSkus = [...newSelectedSkus];
      skusCondition.value = {
        selectedSkus,
        operator,
        operator2,
      };
    }
    return { optionExists, skusCondition };
  }

  onSkuRemove(
    conditionIndex: number,
    skuIndex: number,
    currentConditionsSelectValue,
    currentSelectedSkus,
    currentOperatorValue,
    currentOperator2Value,
  ): any {
    const operator = currentOperatorValue[conditionIndex];
    const operator2 = currentOperator2Value[conditionIndex];
    // must use copy array because we get the real ref to filterValueArray from table
    const skusCondition = {
      ...currentConditionsSelectValue[conditionIndex],
    };
    const removeSelectedSkus: string[] = [...currentSelectedSkus[conditionIndex]];
    removeSelectedSkus.splice(skuIndex, 1);
    const selectedSkus = [...removeSelectedSkus];
    skusCondition.value = {
      selectedSkus,
      operator,
      operator2,
    };
    currentSelectedSkus[conditionIndex] = removeSelectedSkus;
    return skusCondition;
  }

  prepareOperatorOptions(
    options: DropdownOption[],
    conditionSelectIndex,
    type: RulesAmountType,
    currentConditionsSelectValue,
    currentOperatorValue,
  ) {
    const newOptions = [...options];
    const { indexes, chosenOperatorsValues } = this.getAmountOperatorsAndIndexes(
      currentConditionsSelectValue,
      currentOperatorValue,
      type,
    );

    if (chosenOperatorsValues.length) {
      if (type == RulesAmountType.TAGS || type == RulesAmountType.SKUS) {
        // unlimited selected options
        return newOptions;
      }
      for (const [i, chosenOperatorValue] of chosenOperatorsValues.entries()) {
        if (conditionSelectIndex == indexes[i] && (!chosenOperatorsValues || chosenOperatorsValues?.length <= 1)) {
          return newOptions;
        }
        if (
          conditionSelectIndex != indexes[i] &&
          (chosenOperatorValue == AmountOperatorsValues.LESS_THAN ||
            chosenOperatorValue == AmountOperatorsValues.LESS_THAN_OR_EQUALS)
        ) {
          return newOptions?.map((option) => {
            if (
              option?.value != AmountOperatorsValues.GREATER_THAN &&
              option?.value != AmountOperatorsValues.GREATER_THEN_OR_EQUALS
            ) {
              return {
                ...option,
                disabled: true,
              };
            }
            return option;
          });
        } else if (
          conditionSelectIndex != indexes[i] &&
          (chosenOperatorValue == AmountOperatorsValues.GREATER_THAN ||
            chosenOperatorValue == AmountOperatorsValues.GREATER_THEN_OR_EQUALS)
        ) {
          return newOptions?.map((option) => {
            if (
              option?.value != AmountOperatorsValues.LESS_THAN &&
              option?.value != AmountOperatorsValues.LESS_THAN_OR_EQUALS
            ) {
              return {
                ...option,
                disabled: true,
              };
            }
            return option;
          });
        }
      }
    }
    return newOptions;
  }

  getAmountOperatorsAndIndexes(currentConditionsSelectValue, currentOperatorValue, type: RulesAmountType) {
    const indexes = [];
    const chosenOperatorsValues = [];
    currentConditionsSelectValue.map((currentCondition, index) => {
      switch (type) {
        case RulesAmountType.WEIGHT:
          if (currentCondition?.name == 'totalOrderWeight') {
            indexes.push(index);
            chosenOperatorsValues.push(currentOperatorValue[index]?.value);
          }
          break;
        case RulesAmountType.TOTAL_VALUE:
          if (currentCondition?.name == 'totalOrderValue') {
            indexes.push(index);
            chosenOperatorsValues.push(currentOperatorValue[index]?.value);
          }
          break;
        case RulesAmountType.ITEM_QUANTITY:
          if (currentCondition?.name == 'itemQuantity') {
            indexes.push(index);
            chosenOperatorsValues.push(currentOperatorValue[index]?.value);
          }
          break;
        case RulesAmountType.TAGS:
          if (currentCondition?.name == 'orderTag') {
            indexes.push(index);
            chosenOperatorsValues.push(currentOperatorValue[index]?.value);
          }
          break;
        case RulesAmountType.SKUS:
          if (currentCondition?.name == 'orderSku') {
            indexes.push(index);
            chosenOperatorsValues.push(currentOperatorValue[index]?.value);
          }
          break;
        case RulesAmountType.GENERIC:
          indexes.push(index);
          chosenOperatorsValues.push(currentOperatorValue[index]?.value);
          break;
      }
    });
    return { indexes, chosenOperatorsValues };
  }

  isAmountConditionDisabled(currentConditionsSelectValue, currentOperatorValue, checkIndex, type) {
    const { indexes, chosenOperatorsValues } = this.getAmountOperatorsAndIndexes(
      currentConditionsSelectValue,
      currentOperatorValue,
      type,
    );
    //check if it is the condition itself, if it is, allow this option
    if ((indexes.length > 0 && indexes[0] == checkIndex) || (indexes.length > 1 && indexes[1] == checkIndex)) {
      return false;
    }
    //if weight operator is Equals or Not Equals, could not be another weight condition.
    if (
      chosenOperatorsValues?.some(
        (value: RulesOperator) =>
          [AmountOperatorsValues.EQUALS, AmountOperatorsValues.NOT_EQUALS].indexOf(
            (value + '') as AmountOperatorsValues,
          ) > -1,
      )
    ) {
      return true;
    }
    if (
      (chosenOperatorsValues?.some((value: RulesOperator) => AmountOperatorsValues.GREATER_THAN == value + '') ||
        chosenOperatorsValues?.some(
          (value: RulesOperator) => AmountOperatorsValues.GREATER_THEN_OR_EQUALS == value + '',
        )) &&
      (chosenOperatorsValues?.some((value: RulesOperator) => AmountOperatorsValues.LESS_THAN == value + '') ||
        chosenOperatorsValues?.some((value: RulesOperator) => AmountOperatorsValues.LESS_THAN_OR_EQUALS == value + ''))
    ) {
      return true;
    }
    return false;
  }

  isOptionAlreadyChosen(currentCondition, conditionSelectIndex, currentConditionsSelectValue, currentOperatorValue) {
    return currentConditionsSelectValue?.find((condition, loopIndex) => {
      if (conditionSelectIndex != loopIndex) {
        if (currentCondition?.name == 'totalOrderWeight') {
          return this.isAmountConditionDisabled(
            currentConditionsSelectValue,
            currentOperatorValue,
            conditionSelectIndex,
            RulesAmountType.WEIGHT,
          );
        } else if (currentCondition?.name == 'totalOrderValue') {
          return this.isAmountConditionDisabled(
            currentConditionsSelectValue,
            currentOperatorValue,
            conditionSelectIndex,
            RulesAmountType.TOTAL_VALUE,
          );
        } else if (currentCondition?.name == 'itemQuantity') {
          return this.isAmountConditionDisabled(
            currentConditionsSelectValue,
            currentOperatorValue,
            conditionSelectIndex,
            RulesAmountType.ITEM_QUANTITY,
          );
        } else if (
          currentCondition?.name == 'boxChosen' ||
          currentCondition?.name == 'orderTag' ||
          currentCondition?.name == 'orderSku'
        ) {
          return false;
        } else {
          return condition?.name == currentCondition?.name;
        }
      } else {
        return false;
      }
    });
  }

  initialPackagesOptions(ruleTemplateService, conditions) {
    return ruleTemplateService.convertValuesToDropDownOptions({
      conditions: conditions[0].values,
      conditionName: 'boxChosen',
      labelField: 'name',
      multiSelect: true,
    });
  }

  initCreatedOnOptions(conditions) {
    return this.convertValuesToDropDownOptions({
      conditions: conditions[0].values,
      conditionName: RulesConditions.CreatedOn,
      labelField: 'label',
      valueField: 'value',
      multiSelect: true,
    });
  }

  initTimeWindowWeekDaysOptions(conditions) {
    return this.convertValuesToDropDownOptions({
      conditions: conditions[0].values,
      conditionName: RulesConditions.Time,
      labelField: 'label',
      valueField: 'value',
      multiSelect: true,
    });
  }

  initFulfillmentStatusOptions(conditions) {
    return this.convertValuesToDropDownOptions({
      conditions: conditions[0].values,
      conditionName: RulesConditions.OrderFulfillmentStatus,
      labelField: 'label',
      valueField: 'value',
    });
  }
  convertValuesToDropDownOptions(object: {
    conditions?: any[];
    conditionName?: string;
    labelField: string;
    multiSelect?: boolean;
    values?: any[];
    valueField?: string;
    valueField2?: string;
    typeField?: string;
    displayLabelFunc?: Function;
    isRawValues?: boolean;
  }) {
    const values =
      object.values || object.conditions?.find((condition) => condition.name == object.conditionName)?.values;
    return values?.map((value, index) => {
      let option = null;
      if (object.isRawValues) {
        option = { value: value, label: object.displayLabelFunc ? object.displayLabelFunc(value) : value };
      } else {
        option = {
          value: object.valueField ? value[object.valueField] : value.id,
          label: object.displayLabelFunc ? object.displayLabelFunc(value[object.labelField]) : value[object.labelField],
        };
      }

      if (object.multiSelect) {
        option['checked'] = false;
      }
      if (object.typeField) {
        option['type'] = value[object.typeField];
      }
      if (object.valueField2) {
        option['value2'] = value[object.valueField2];
      }
      return option;
    });
  }
  setHeadsUpNotifications(action: any, currentActionSelectValue: AutomationRulesActions.SelectedAction) {
    const headsUp = [];
    if (
      currentActionSelectValue &&
      currentActionSelectValue.name.includes('changeOrderStatus') &&
      currentActionSelectValue.value == 'stepAssign'
    ) {
      headsUp.push(`<span>Orders can't be automatically approved to the Assign screen if they're flagged</span>
      <span>for missing/incorrect details, fraud risk, cash-on-delivery, or anything else.</span>`);
    }
    if (currentActionSelectValue?.name === RulesActions.SplitOrderByStrategy) {
      if (action?.value?.value !== 'line_item') {
        headsUp.push(`Orders can't be split by this Quickflow if they're flagged as Split Required.`);
        headsUp.push(
          `This new Quickflow splits orders, which may affect the Quickflows that follow this one (those lower on the Quickflows list).`,
        );
      } else {
        headsUp.push(
          `This new Quickflow splits orders, which may affect the Quickflows that follow this one (those lower on the Quickflows list).`,
        );
      }
    } else if (currentActionSelectValue && currentActionSelectValue.isStatusChanger) {
      headsUp.push(
        `This new Quickflow pushes orders to another screen, which may affect the Quickflows that follow this one (those lower on the Quickflows list).`,
      );
    } else if (
      (currentActionSelectValue?.name === RulesActions.SetRecommendedCarrier &&
        action?.value?.shipmentPreferncesFields?.enforceCarrierSelection) ||
      (currentActionSelectValue?.name === RulesActions.SetReturnPreferences &&
        action?.value?.enforceCarrierSelection) ||
      (currentActionSelectValue?.name === RulesActions.SmartShipOn && action?.value?.enforceCarrierSelection)
    ) {
      headsUp.push(
        `Carrier selection will be enforced but if your shipping conditions aren't met, no carrier will be assigned at all.`,
      );
    } else if (currentActionSelectValue?.name === RulesActions.SetWeightToSKU) {
      headsUp.push(
        `Once the weight has already been set (either manually by the merchant or by Quickflow previously), this action will not run again`,
      );
    }
    return headsUp;
  }
}
